const CONTACT_IDENTIFIER_KEYS = [
  'identificador_del_contacto',
  'identificador_contacto',
  'identificador_del_contanto',
  'contact_identifier',
  'contact_id',
];

const CONTACT_USERNAME_KEYS = [
  'username',
  'user_name',
  'whatsapp_username',
  'wa_username',
  'instagram_username',
  'telegram_username',
  'handle',
  'nickname',
];

const CONTACT_COMPANY_KEYS = [
  'company_name',
  'company',
  'empresa',
  'business_name',
  'organization',
  'organisation',
];

const normalizeIdentifier = value => {
  if (value === null || value === undefined) {
    return '';
  }

  const parsedValue = String(value).trim();
  return parsedValue;
};

const getIdentifierFromRecord = record => {
  if (!record || typeof record !== 'object') {
    return '';
  }

  const matchingKey = CONTACT_IDENTIFIER_KEYS.find(key =>
    normalizeIdentifier(record[key])
  );
  if (matchingKey) {
    return normalizeIdentifier(record[matchingKey]);
  }

  const inferredKey = Object.keys(record).find(key => {
    const normalizedKey = key.toLowerCase();
    return (
      normalizedKey.includes('identificador') &&
      (normalizedKey.includes('contact') || normalizedKey.includes('contanto'))
    );
  });

  if (!inferredKey) {
    return '';
  }

  return normalizeIdentifier(record[inferredKey]);
};

const getValueFromRecord = (record, keys) => {
  if (!record || typeof record !== 'object') {
    return '';
  }

  const matchingKey = keys.find(key => normalizeIdentifier(record[key]));
  if (matchingKey) {
    return normalizeIdentifier(record[matchingKey]);
  }

  return '';
};

const normalizeSourceId = value =>
  normalizeIdentifier(value).replace(/^(whatsapp:|sms:)/i, '');

const formatUsername = value => {
  const username = normalizeIdentifier(value);
  if (!username) {
    return '';
  }

  return username.startsWith('@') ? username : `@${username}`;
};

const normalizeForComparison = value =>
  normalizeIdentifier(value).replace(/^@/, '').toLowerCase();

const buildUniqueValues = values => {
  const seen = new Set();

  return values.filter(value => {
    const normalizedValue = normalizeForComparison(value);
    if (!normalizedValue || seen.has(normalizedValue)) {
      return false;
    }

    seen.add(normalizedValue);
    return true;
  });
};

export const getContactIdentifier = contact => {
  const customIdentifier = getIdentifierFromRecord(contact?.custom_attributes);
  if (customIdentifier) {
    return customIdentifier;
  }

  return getIdentifierFromRecord(contact?.additional_attributes);
};

export const getContactUsername = contact => {
  const customUsername = getValueFromRecord(
    contact?.custom_attributes,
    CONTACT_USERNAME_KEYS
  );
  if (customUsername) {
    return formatUsername(customUsername);
  }

  return formatUsername(
    getValueFromRecord(contact?.additional_attributes, CONTACT_USERNAME_KEYS)
  );
};

export const getContactCompany = contact => {
  const customCompany = getValueFromRecord(
    contact?.custom_attributes,
    CONTACT_COMPANY_KEYS
  );
  if (customCompany) {
    return normalizeIdentifier(customCompany);
  }

  return normalizeIdentifier(
    getValueFromRecord(contact?.additional_attributes, CONTACT_COMPANY_KEYS)
  );
};

export const getContactSourceId = contact =>
  normalizeSourceId(
    contact?.source_id || contact?.contact_inboxes?.[0]?.source_id
  );

export const getContactDisplayName = contact => {
  const name = normalizeIdentifier(contact?.name);
  const phoneNumber = normalizeIdentifier(contact?.phone_number);
  const username = getContactUsername(contact);
  const identifier = getContactIdentifier(contact);
  const sourceId = getContactSourceId(contact);

  return name || phoneNumber || username || identifier || sourceId;
};

export const getContactSecondaryDetails = contact => {
  const displayName = getContactDisplayName(contact);
  const phoneNumber = normalizeIdentifier(contact?.phone_number);
  const username = getContactUsername(contact);
  const identifier = getContactIdentifier(contact);
  const sourceId = getContactSourceId(contact);

  return buildUniqueValues([
    phoneNumber,
    username,
    identifier,
    sourceId,
  ]).filter(value => {
    return (
      normalizeForComparison(value) !== normalizeForComparison(displayName)
    );
  });
};

export const getContactCopyValue = contact => {
  const [primaryDetail] = getContactSecondaryDetails(contact);
  return (
    primaryDetail ||
    normalizeIdentifier(contact?.phone_number) ||
    getContactIdentifier(contact) ||
    getContactSourceId(contact)
  );
};
